<template>
  <MainHeader />
  <MainSidebar />
  <div class="main-content bg_gray d-flex flex-column transition overflow-hidden">
    <BreadcrumbMenu pageTitle="Reglas de Negocio" />

    <div class="flex-grow-1 overflow-auto px-3 pb-5">

      <!-- Aviso -->
      <div class="alert alert-info d-flex gap-2 align-items-center mb-4" style="font-size:13px">
        <span>📋</span>
        <span>Este documento recoge las reglas de negocio confirmadas con Rosa y el equipo. Se actualiza cada vez que se aprende algo nuevo del flujo real de la tienda.</span>
      </div>

      <!-- CÓDIGOS Y BARCODES -->
      <div class="card mb-4">
        <div class="card-header fw-bold" style="background:#f0f4ff">
          🏷️ Códigos de producto y barcodes
        </div>
        <div class="card-body" style="font-size:13px">
          <p class="text-muted small mb-3">Confirmado por Rosa — 28/06/2026</p>

          <div class="row g-3">
            <div class="col-md-6">
              <div class="border rounded p-3 h-100" style="background:#f8f9fa">
                <div class="fw-semibold mb-2">Código de proveedor</div>
                <ul class="mb-0 ps-3">
                  <li>Siempre <strong>único e irrepetible</strong></li>
                  <li>Sin excepción: hombre, mujer, unisex, niño, niña</li>
                  <li>Cada modelo/color/género tiene su propio código</li>
                </ul>
              </div>
            </div>
            <div class="col-md-6">
              <div class="border rounded p-3 h-100" style="background:#f8f9fa">
                <div class="fw-semibold mb-2">Barcode (EAN)</div>
                <ul class="mb-0 ps-3">
                  <li>Siempre <strong>único e irrepetible</strong></li>
                  <li>Lo que lo diferencia es la <strong>talla</strong>, no el género</li>
                  <li>Talla 8 = mismo barcode para hombre o mujer</li>
                  <li>Talla 8 ≠ Talla 9 → barcodes distintos siempre</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="mt-3 p-3 rounded border border-warning" style="background:#fffbea">
            <div class="fw-semibold mb-2">⚠️ UNISEX — lo que significa y lo que NO significa</div>
            <div class="row g-2" style="font-size:12px">
              <div class="col-md-6">
                <div class="text-success fw-semibold mb-1">✓ Lo que SÍ significa</div>
                <ul class="mb-0 ps-3">
                  <li>El mismo producto sirve para hombre y mujer</li>
                  <li>Aplica en calzado y textil (polos)</li>
                  <li>También existe UNISEX para niños</li>
                </ul>
              </div>
              <div class="col-md-6">
                <div class="text-danger fw-semibold mb-1">✗ Lo que NO significa</div>
                <ul class="mb-0 ps-3">
                  <li>NO implica que el barcode sea compartido por género</li>
                  <li>Si hombre talla 9 y mujer talla 6 → barcodes distintos (distinta talla)</li>
                  <li>Si ambos coinciden en talla 8 → mismo barcode (misma talla, no por ser unisex)</li>
                </ul>
              </div>
            </div>
          </div>

          <div class="mt-3 p-3 rounded" style="background:#f0fdf4;border:1px solid #86efac">
            <div class="fw-semibold mb-1">Resumen práctico</div>
            <code style="font-size:12px">
              código_proveedor → identifica modelo + color + género<br>
              barcode → identifica talla específica dentro de ese modelo<br>
              Ambos son únicos. No hay excepciones.
            </code>
          </div>
        </div>
      </div>

      <!-- PRECIO DE VENTA -->
      <div class="card mb-4">
        <div class="card-header fw-bold" style="background:#f0f4ff">
          💰 Precio de venta
        </div>
        <div class="card-body" style="font-size:13px">
          <p class="text-muted small mb-3">Confirmado por Rosa — 28/06/2026</p>

          <div class="row g-3">
            <div class="col-md-4">
              <div class="border rounded p-3 h-100 text-center" style="background:#f8f9fa">
                <div class="fs-4 mb-1">📄</div>
                <div class="fw-semibold mb-1">XML / PDF Adidas</div>
                <div class="text-muted small">Solo trae precio de <strong>costo</strong> (lo que paga la tienda). El precio de venta sugerido que Adidas envía <strong>no se usa</strong>.</div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="border rounded p-3 h-100 text-center" style="background:#f8f9fa">
                <div class="fs-4 mb-1">📊</div>
                <div class="fw-semibold mb-1">% de margen</div>
                <div class="text-muted small">La empresa aplica un <strong>% de margen de utilidad</strong> sobre el precio de costo. Este % lo conocen de años — no depende de documento externo.</div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="border rounded p-3 h-100 text-center" style="background:#f8f9fa">
                <div class="fs-4 mb-1">🏪</div>
                <div class="fw-semibold mb-1">Resultado</div>
                <div class="text-muted small">El precio final coincide con el de otras tiendas del mercado. Es <strong>igual en ambas sedes</strong> (939 y 1179) y para todas las tallas del mismo modelo.</div>
              </div>
            </div>
          </div>

          <div class="mt-3 p-3 rounded border-start border-4 border-warning" style="background:#fffbea">
            <strong>Pendiente definir:</strong> ¿Cuál es el % de margen exacto que usan? Con ese dato el sistema puede calcular el precio automáticamente en la pantalla de Recepción, y Rosa solo lo verifica antes de confirmar.
          </div>
        </div>
      </div>

      <!-- FLUJO DE COMPRAS -->
      <div class="card mb-4">
        <div class="card-header fw-bold" style="background:#f0f4ff">
          🔄 Flujo de una compra — de la factura al stock
        </div>
        <div class="card-body" style="font-size:13px">
          <div class="d-flex flex-column gap-2">
            <div v-for="(paso, i) in pasos" :key="i"
              class="d-flex align-items-start gap-3 p-3 rounded border"
              :style="paso.ok ? 'background:#f0fdf4' : 'background:#f8f9fa'">
              <div class="fw-bold text-muted" style="min-width:24px">{{ i + 1 }}</div>
              <div>
                <div class="fw-semibold">{{ paso.titulo }}</div>
                <div class="text-muted small mt-1">{{ paso.desc }}</div>
                <span v-if="paso.ok" class="badge bg-success mt-1" style="font-size:10px">✓ Implementado</span>
                <span v-else class="badge bg-warning text-dark mt-1" style="font-size:10px">Pendiente</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- GÉNEROS (tbtalla) -->
      <div class="card mb-4">
        <div class="card-header fw-bold" style="background:#f0f4ff">
          👥 Géneros — tabla tbtalla
        </div>
        <div class="card-body p-0">
          <table class="table table-sm mb-0" style="font-size:13px">
            <thead class="table-light">
              <tr>
                <th class="ps-3">idtalla</th>
                <th>Nombre</th>
                <th>Descripción</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="g in generos" :key="g.id">
                <td class="ps-3"><code>{{ g.id }}</code></td>
                <td>{{ g.nombre }}</td>
                <td class="text-muted small">{{ g.desc }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ALMACENES -->
      <div class="card mb-4">
        <div class="card-header fw-bold" style="background:#f0f4ff">
          🏬 Almacenes / Sedes
        </div>
        <div class="card-body p-0">
          <table class="table table-sm mb-0" style="font-size:13px">
            <thead class="table-light">
              <tr>
                <th class="ps-3">idalmacen</th>
                <th>Sede</th>
                <th>Notas</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="ps-3"><code>1</code></td>
                <td>Sede 939</td>
                <td class="text-muted small">Almacén principal</td>
              </tr>
              <tr>
                <td class="ps-3"><code>15</code></td>
                <td>Sedes 1179 / 11155</td>
                <td class="text-muted small">Segundo almacén</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";

export default {
  name: "ReglasNegocioPage",
  components: { MainHeader, MainSidebar, BreadcrumbMenu },
  data() {
    return {
      pasos: [
        {
          titulo: "Importar XML + PDF",
          desc: "Rosa sube la factura electrónica (XML SUNAT) y el PDF con desglose de tallas. El sistema parsea los productos y crea el registro de compra. Los productos nuevos se crean en el catálogo con precio = 0.",
          ok: true,
        },
        {
          titulo: "Recepción de mercadería",
          desc: "Cuando llega la mercadería físicamente, el operador abre la compra en Recepción. Por cada producto: asigna género/línea (si es nuevo), ingresa precio de venta, escanea el barcode de cada talla y confirma las unidades recibidas.",
          ok: true,
        },
        {
          titulo: "Confirmar lo recibido",
          desc: "El operador confirma los ítems que llegaron. El stock se actualiza en tbstockactualalmacen. Los ítems sin confirmar quedan pendientes para la próxima entrega (recepción parcial).",
          ok: true,
        },
        {
          titulo: "Cerrar recepción",
          desc: "Cuando toda la mercadería de la factura llegó, se cierra la recepción. La compra pasa a historial.",
          ok: true,
        },
        {
          titulo: "Auto-cálculo de precio por % de margen",
          desc: "Pendiente definir el % exacto con Rosa. Con ese dato el sistema calcula el precio automáticamente desde el costo del XML — Rosa solo verifica.",
          ok: false,
        },
      ],
      generos: [
        { id: -1, nombre: "Sin especificar", desc: "Producto sin género asignado — debe corregirse" },
        { id: 0,  nombre: "Infante",         desc: "Bebé / tallas en meses (18M, 2T, 3T)" },
        { id: 1,  nombre: "Niño",            desc: "Niño varón" },
        { id: 2,  nombre: "Dama",            desc: "Mujer adulta" },
        { id: 3,  nombre: "Caballero",       desc: "Hombre adulto" },
        { id: 4,  nombre: "Unisex adulto",   desc: "Sirve para hombre y mujer — mismo barcode por talla" },
        { id: 5,  nombre: "Niña",            desc: "Niña" },
        { id: 6,  nombre: "Unisex niño",     desc: "Sirve para niño y niña" },
      ],
    };
  },
};
</script>
